import {useIsFeatureActive} from '@aboutyou/feature-flags';
import {GetDiscountsResponse_Discount} from '@aboutyou/grpc-clients/tadarida/aysa_api/services/campaigns/v1/service.types';
import {useTranslation} from '@aboutyou/i18n';
import {isExternalLink, useHistory} from '@aboutyou/router';
import {Slide} from '@aboutyou/transitions';
import {
  COLORS,
  CountdownBoxed,
  GenericIconName,
  Icon,
  InfoCircleWithDialog,
  Text,
  Z_INDEXES,
  useTimedOut,
} from '@aboutyou/ui-components';
import React, {useState} from 'react';
import styled from 'styled-components';
import {useGetGenderBanner} from '../../backend-services/Campaigns/hooks/useGetGenderBanner';
import {useIsUrgencyTriggersDisabled} from '../../shared-deprecated/useIsUrgencyTriggersDisabled';
import {useCampaignOverlay} from './hooks/useCampaignOverlay';

export const CampaignOverlay = () => {
  const {shouldShow, discount} = useCampaignOverlay();
  const {t} = useTranslation();
  const getGenderBanner = useGetGenderBanner();
  const [open, setOpen] = useState(shouldShow);
  const timedOut = useTimedOut(2000);
  const history = useHistory();
  const {isActive: isPriceDisplayOverhaulActive} = useIsFeatureActive(
    'price_display_overhaul',
  );
  const hideCountdown = useIsUrgencyTriggersDisabled();

  if (!shouldShow) {
    return null;
  }

  const priceReduction = (discount as
    | GetDiscountsResponse_Discount
    | undefined)!;

  const genderTopBanner = getGenderBanner(discount!);

  const onClick = () => {
    if (discount?.linkTarget && isExternalLink(discount?.linkTarget.url)) {
      window.open(discount?.linkTarget.url, '_blank');
    } else if (discount?.linkTarget) {
      history.push(discount.linkTarget.url);
    }
  };

  const titleTranslation = isPriceDisplayOverhaulActive
    ? t('Don’t miss the DEAL')
    : t('Don’t miss the EXTRA SALE');

  return (
    <Wrapper>
      <PositionAbsolute>
        <Slide mounted={open && timedOut} direction={'down'} duration={400}>
          <CardWrapper>
            <CampaignOverlayHeader
              $campaignColor={genderTopBanner?.backgroundColor}
              onClick={() => setOpen(false)}
            >
              {genderTopBanner?.showCountdown ? (
                <CountdownBoxed
                  endDate={priceReduction.campaign.period.endDate}
                  size="small"
                  backgroundColor={genderTopBanner.backgroundColor}
                  light
                />
              ) : (
                <Text size="px10" color="White">
                  {t('Be quick!')}
                </Text>
              )}
              <Icon
                asset={GenericIconName.Close}
                size="XS"
                fill={COLORS.White}
              />
            </CampaignOverlayHeader>
            <CampaignOverlayBody onClick={onClick}>
              <Icon
                asset={GenericIconName.Sale}
                fill={genderTopBanner?.backgroundColor}
                size="XL"
              />
              <TextWrapper>
                <Text size="px14" weight="mediumHeavy">
                  {titleTranslation}
                </Text>
                <Text size="px12">{t('Grab your savings now!')}</Text>
              </TextWrapper>
              <div>
                {genderTopBanner?.conditionText && (
                  <StyledInfoCircleWithDialog
                    conditions={genderTopBanner?.conditionText}
                    fill={COLORS.Grey2}
                    hideCountdown={hideCountdown}
                  />
                )}
              </div>
            </CampaignOverlayBody>
          </CardWrapper>
        </Slide>
      </PositionAbsolute>
    </Wrapper>
  );
};

const CampaignOverlayHeader = styled.div<{$campaignColor?: string}>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  background-color: ${props => props.$campaignColor};
  padding: 0 0.5rem;
`;

const CardWrapper = styled.div`
  border-radius: 0.5rem;
  box-shadow: 0px 4px 15px 0px rgba(0, 0, 0, 0.2);
  overflow: hidden;
  margin: 0.75rem 1rem 1rem;
`;

const Wrapper = styled.div`
  position: relative;
  z-index: ${Z_INDEXES.CAMPAIGN_OVERLAY};
`;

const PositionAbsolute = styled.div`
  position: absolute;
  width: 100%;
`;
const CampaignOverlayBody = styled.div`
  display: flex;
  gap: 0.5rem;
  padding: 0.75rem 0.75rem 1rem;
  align-items: center;
  justify-content: space-between;
  background: ${COLORS.White};
`;

const TextWrapper = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
`;

const StyledInfoCircleWithDialog = styled(InfoCircleWithDialog)`
  position: inherit;
  top: auto;
  right: auto;
  left: auto;
`;
